import {
  type BoxCategory,
  resolveStorefrontFeatures,
  type SocialLinks,
  type StorefrontFeatureKey,
  type TenantModuleKey,
  type TenantModules,
  type TenantRole,
} from "@loot-os/api-contract";
import type { LinkProps } from "@tanstack/react-router";
import {
  BarChart3,
  FlaskConical,
  KeyRound,
  LayoutDashboard,
  type LucideIcon,
  Package,
  Settings,
  Sparkles,
  Store,
  Truck,
  Users,
  UsersRound,
} from "lucide-react";
import { ShopifyIcon } from "./shopifyIcon";

/**
 * A static page under the tenant: the router's own union below `/tenants/$tenantId`, minus the
 * routes that take a second param (`boxes/$boxId`, `users/$userId`), so every path here links
 * with `params={{ tenantId }}` alone.
 */
export type TenantNavPath = Exclude<
  Extract<LinkProps["to"], `/tenants/$tenantId${string}`>,
  `/tenants/$tenantId/${string}$${string}`
>;

/** A hostname row as a sidebar status reads it: the three columns Domains' dot decides on. */
export interface StorefrontDomainSwitch {
  /** `subdomain` (provisioned under the root) or `custom` (tenant-attached). */
  type: string;
  /** `active` is the only status the resolver serves. */
  status: string;
  isPrimary: boolean;
}

/**
 * What a sidebar status reads from the `storefront.get` query every tenant page already runs,
 * for the active environment: the `config` row's switches, plus the environment's `domains` and
 * whether a Shopify shop is linked beside it.
 */
export interface StorefrontSwitches {
  enabled: boolean;
  features: Partial<Record<StorefrontFeatureKey, boolean>>;
  intercomEnabled: boolean;
  /** Parsed; `{}` when none is set. */
  socialLinks: SocialLinks;
  /** The box categories the boxes page offers, parsed; `[]` when none is switched on. */
  boxCategories: readonly BoxCategory[];
  /** Every hostname of the environment, the provisioned subdomain included. */
  domains: readonly StorefrontDomainSwitch[];
  /** A Shopify shop is linked to the environment and active (not uninstalled). */
  shopifyLinked: boolean;
}

/** A hub's section page: folded under the hub in the sidebar, named after it in the breadcrumb. */
export interface TenantNavChild {
  to: TenantNavPath;
  label: string;
  /** Extra terms the palette finds the page by: the names of pages folded into this one. */
  keywords?: readonly string[];
  /**
   * For a page with one state worth a glance: whether the active environment has its switch on
   * (Storefront, Payments, Battles, Chat, Forge, Live breaks, Support chat), for Social links at least one
   * network set, for Categories at least one category switched on, for Domains an active custom domain as its primary, or for Shopify an active
   * linked shop. The sidebar shows a green dot on the row while it does and a muted one
   * otherwise, so the state reads without opening the page; the screen reader hears
   * "{label} enabled" / "{label} disabled".
   */
  status?: (config: StorefrontSwitches) => boolean;
  /**
   * A section page that serves a higher role than its hub (the hub's own when unset): the
   * sidebar and the palette hide it below that, and a hub left with no section reads as a
   * plain page.
   */
  minRole?: TenantRole;
  /**
   * The tenant module (`tenant_settings.modules`) the page belongs to: while platform staff have
   * it off, the sidebar and the palette leave the page out and its URL shows a notice instead. A
   * hub's sections inherit its module, and a hub left with no section reads as a plain page.
   */
  module?: TenantModuleKey;
}

/**
 * A hub's section with pages of its own folded beneath it, one level deeper (Storefront → Live
 * breaks → Hosts). The section's own row is a page; its pages inherit its `minRole` and
 * `module`, else the hub's.
 */
export interface TenantNavSection extends TenantNavChild {
  children?: readonly TenantNavChild[];
}

export interface TenantNavItem extends TenantNavChild {
  icon: LucideIcon;
  /** Highlight only on the exact path, for the overview, whose path prefixes every other. */
  exact: boolean;
  /**
   * The tenant role the page serves: the sidebar and the palette hide it below that, and the
   * server forbids it regardless.
   */
  minRole: TenantRole;
  /** The section pages of a hub, in the hub's own order. They inherit `minRole` unless they set one. */
  children?: readonly TenantNavSection[];
}

/** One section of the sidebar in tenant scope: a label (none for the top) and its pages. */
export interface TenantNavGroup {
  key: string;
  label: string | null;
  items: readonly TenantNavItem[];
}

/**
 * The pages inside a tenant, rendered with the current $tenantId param and grouped by what
 * they read: the top section is the active environment's pages (every `*EnvironmentProcedure`
 * reader), "Tenant" the admin pages: those that are the same in every environment
 * (`tenantProcedure` / `adminProcedure` readers) plus API keys, which reads the environment's
 * keys but is an admin's integration page, not the store's. A mixed page sits
 * where its primary read lives: Settings (tenant settings, plus the platform-only
 * per-environment KYC switches) under Tenant; Payments (the Stripe account is the tenant's,
 * but the switch, the packs, the purchases and the disputes are the environment's) and Domains
 * (list and add per environment; retry, remove and setPrimary tenant-wide) as Storefront
 * sections, since what they configure is the environment's storefront.
 *
 * Kept apart from `PLATFORM_TOOLS` (`platformTools.ts`) rather than merged: these take a route
 * param, and one `to` union spanning both would make `<Link params>` unassignable. The sidebar,
 * the ⌘K palette and the breadcrumb trail all read this list, so a page added here needs no
 * entry anywhere else.
 */
const NAV = [
  {
    key: "store",
    label: null,
    items: [
      {
        to: "/tenants/$tenantId",
        label: "Overview",
        icon: LayoutDashboard,
        exact: true,
        minRole: "viewer",
      },
      {
        to: "/tenants/$tenantId/boxes",
        label: "Boxes",
        icon: Package,
        exact: false,
        minRole: "viewer",
      },
      {
        // No module: a box opens with its style on the storefront, the Shopify overlay and the
        // API alike, so the page stays with the storefront module off.
        to: "/tenants/$tenantId/openers",
        label: "Openers",
        icon: Sparkles,
        exact: false,
        minRole: "viewer",
        keywords: [
          "opener style",
          "spinner",
          "animation",
          "opening animation",
          "reel",
          "neon slam",
          "neon pack",
          "retail box",
        ],
      },
      {
        to: "/tenants/$tenantId/users",
        label: "Shoppers",
        icon: Users,
        exact: false,
        minRole: "viewer",
      },
      {
        to: "/tenants/$tenantId/analytics",
        module: "analytics",
        label: "Analytics",
        icon: BarChart3,
        exact: false,
        minRole: "viewer",
        keywords: ["page views", "rollups"],
        children: [
          {
            // Viewer-level like the hub: it reads the rollups, never raw events.
            to: "/tenants/$tenantId/analytics/insights",
            label: "Insights",
            keywords: ["trends", "compare events", "chart", "series", "mixpanel"],
          },
          {
            // Admin because the page reads `analytics.events`, an adminEnvironmentProcedure.
            to: "/tenants/$tenantId/analytics/events",
            label: "Event log",
            minRole: "admin",
            keywords: ["events", "raw events", "line items", "timeline", "session"],
          },
        ],
      },
      {
        to: "/tenants/$tenantId/storefront",
        module: "storefront",
        label: "Storefront",
        icon: Store,
        exact: false,
        minRole: "viewer",
        // The row itself is the Storefront page: the status switch, then a tab each for General
        // (branding), Banners, Theme and Terminology, hence the banner, theme and wording terms
        // here. The sections beneath it run address → brand → store → policies; each page gates
        // its own edits on `can("editor")`.
        keywords: [
          "branding",
          "logo",
          "favicon",
          "banners",
          "slides",
          "carousel",
          "hero",
          "theme",
          "rarity",
          "colors",
          "palette",
          "terminology",
          "wording",
          "names",
          "labels",
        ],
        status: (config) => config.enabled,
        children: [
          {
            // Admin because the page reads `domains.list`, an adminEnvironmentProcedure. The dot
            // says whether the store answers on a domain of the tenant's own: a custom hostname
            // that is active and primary; the provisioned subdomain as primary reads as off.
            to: "/tenants/$tenantId/storefront/domains",
            label: "Domains",
            minRole: "admin",
            keywords: ["custom domain", "dns", "hostname", "certificate", "primary domain"],
            status: (config) =>
              config.domains.some(
                (domain) =>
                  domain.type === "custom" && domain.status === "active" && domain.isPrimary,
              ),
          },
          {
            // No dot: every environment renders its pages, so there is no off state. The page
            // also holds the site frame (header, footer, chat), hence those terms here.
            to: "/tenants/$tenantId/storefront/pages",
            label: "Pages",
            keywords: [
              "layout",
              "page layout",
              "template",
              "home page",
              "box page",
              "sections",
              "header",
              "footer",
              "navigation",
              "chat messages",
              "showroom",
              "spotlight",
              "arcade",
              "split",
              "stacked",
            ],
          },
          {
            to: "/tenants/$tenantId/storefront/social-links",
            label: "Social links",
            status: (config) => Object.values(config.socialLinks).some((url) => Boolean(url)),
          },
          {
            // The dot says whether any category is switched on, so the boxes page shows its row.
            to: "/tenants/$tenantId/storefront/categories",
            label: "Categories",
            keywords: [
              "box categories",
              "category filter",
              "games",
              "pokemon",
              "sports",
              "mtg",
              "filter boxes",
            ],
            status: (config) => config.boxCategories.length > 0,
          },
          {
            // Every member's page: the switch and the packs read at viewer; the Stripe account,
            // purchases, disputes and revenue show to admins alone.
            to: "/tenants/$tenantId/storefront/payments",
            label: "Payments",
            keywords: [
              "currency",
              "stripe",
              "top-up packs",
              "packs",
              "purchases",
              "disputes",
              "revenue",
            ],
            status: (config) => resolveStorefrontFeatures(config.features).balancePurchases,
          },
          {
            to: "/tenants/$tenantId/storefront/battles",
            module: "battles",
            label: "Battles",
            keywords: ["loser prize"],
            status: (config) => resolveStorefrontFeatures(config.features).battles,
          },
          {
            to: "/tenants/$tenantId/storefront/sounds",
            label: "Sounds",
            keywords: ["audio", "volume", "opener"],
          },
          {
            // The switch, then moderation: the room, reports and mutes.
            to: "/tenants/$tenantId/storefront/chat",
            module: "chat",
            label: "Chat",
            keywords: ["chat room", "shopper chat", "moderation", "mute", "reports", "messages"],
            status: (config) => resolveStorefrontFeatures(config.features).chat,
          },
          {
            // The switch, the creator commission and the preset images shoppers build boxes with.
            to: "/tenants/$tenantId/storefront/forge",
            module: "forge",
            label: "Forge",
            keywords: ["community boxes", "forge boxes", "creator cut", "commission", "box images"],
            status: (config) => resolveStorefrontFeatures(config.features).forge,
          },
          {
            // The row is the settings: the switch, the hosting and commission settings, the Host
            // Allow List and the Twitch app. Beneath it, the staff tools for the environment's
            // live breaks. Every page reads at viewer and gates its writes on the role each write
            // needs: editors for the Host Allow List and sponsored streams, admins for the rest.
            to: "/tenants/$tenantId/storefront/live-breaks",
            module: "liveBreaks",
            label: "Live breaks",
            keywords: [
              "live breaks",
              "breaks",
              "hosts",
              "host allow list",
              "allowlist",
              "host commission",
              "commission",
              "stream",
              "twitch",
            ],
            status: (config) => resolveStorefrontFeatures(config.features).liveBreaks,
            children: [
              {
                to: "/tenants/$tenantId/storefront/live-breaks/list",
                label: "All live breaks",
                keywords: [
                  "house live breaks",
                  "house live break",
                  "queue",
                  "end",
                  "force close",
                  "pick",
                  "queue entries",
                ],
              },
              {
                to: "/tenants/$tenantId/storefront/live-breaks/hosts",
                label: "Hosts",
                keywords: ["host allow list", "host rate", "host commission", "commission"],
              },
              {
                to: "/tenants/$tenantId/storefront/live-breaks/blocks",
                label: "Blocks",
                keywords: ["blocked shoppers", "unblock", "house set"],
              },
              {
                to: "/tenants/$tenantId/storefront/live-breaks/playlists",
                label: "Playlists",
                keywords: ["suggested boxes", "box lists"],
              },
              {
                to: "/tenants/$tenantId/storefront/live-breaks/sponsored-streams",
                label: "Sponsored streams",
                keywords: ["twitch streamers", "live-break widget"],
              },
            ],
          },
          {
            // One row for both notification pages: it opens System, and the page's tabs reach
            // Marketing, which is an admin's, since a send reaches every shopper.
            to: "/tenants/$tenantId/storefront/notifications",
            module: "notifications",
            label: "Notifications",
            keywords: [
              "bell",
              "inbox",
              "alerts",
              "messages",
              "push",
              "system notifications",
              "templates",
              "welcome message",
              "order shipped",
              "battle won",
              "marketing",
              "campaigns",
              "announcements",
              "broadcast",
              "scheduled send",
              "promotion",
            ],
          },
          {
            // Admin because every read lists codes, and an unredeemed code is balance to its reader.
            to: "/tenants/$tenantId/storefront/gift-codes",
            label: "Gift codes",
            minRole: "admin",
            keywords: ["gift code manager", "redeem", "giveaway", "influencer", "balance codes"],
          },
          {
            to: "/tenants/$tenantId/storefront/support",
            module: "supportChat",
            label: "Support chat",
            keywords: ["intercom"],
            status: (config) => config.intercomEnabled,
          },
          {
            to: "/tenants/$tenantId/storefront/legal",
            label: "Legal / Consent",
            keywords: ["terms", "privacy", "termly", "cookie consent", "banner"],
          },
        ],
      },
      {
        to: "/tenants/$tenantId/shipping",
        module: "shipping",
        label: "Shipping",
        icon: Truck,
        exact: false,
        minRole: "viewer",
        keywords: ["destinations", "countries", "delivery"],
      },
      {
        // Admin because linking a store and retrying Shopify calls are admin writes; the hub
        // and its sections are the whole integration for this environment (docs/shopify.md).
        // The row itself is the link card, then the overview once a shop is linked.
        to: "/tenants/$tenantId/shopify",
        module: "shopify",
        label: "Shopify",
        icon: ShopifyIcon,
        exact: false,
        minRole: "admin",
        keywords: ["shopify", "loot boxes for shopify", "link code", "sync catalog"],
        status: (config) => config.shopifyLinked,
        children: [
          {
            // Purchases, orders and store credit are tabs of the one page (`?tab=`).
            to: "/tenants/$tenantId/shopify/sales",
            label: "Sales",
            keywords: [
              "purchases",
              "box purchases",
              "checkout",
              "unrevealed",
              "orders",
              "ship orders",
              "shopify orders",
              "dispatch",
              "tracking",
              "store credit",
              "transfers",
              "clawback",
              "sell-back",
            ],
          },
          {
            to: "/tenants/$tenantId/shopify/appearance",
            label: "Appearance",
            keywords: ["opener theme", "overlay", "colors"],
          },
          {
            // Loot Labs' fee on each settled unit, as Shopify usage charges.
            to: "/tenants/$tenantId/shopify/fees",
            label: "Fees",
            keywords: ["usage charges", "billing", "plan", "loot labs fee", "volume fee"],
          },
        ],
      },
    ],
  },
  {
    key: "tenant",
    label: "Tenant",
    items: [
      {
        to: "/tenants/$tenantId/sandboxes",
        label: "Sandboxes",
        icon: FlaskConical,
        exact: false,
        minRole: "admin",
      },
      {
        to: "/tenants/$tenantId/team",
        label: "Team",
        icon: UsersRound,
        exact: false,
        minRole: "admin",
      },
      {
        // The environment's keys (the switcher picks which), filed with the tenant's other
        // admin pages. The internal storefront key is shown here too, above the table.
        to: "/tenants/$tenantId/keys",
        label: "API keys",
        icon: KeyRound,
        exact: false,
        minRole: "admin",
        keywords: ["secret key", "publishable key", "storefront key"],
      },
      {
        to: "/tenants/$tenantId/settings",
        label: "Settings",
        icon: Settings,
        exact: false,
        minRole: "admin",
      },
    ],
  },
] as const satisfies readonly TenantNavGroup[];

export const TENANT_NAV: readonly TenantNavGroup[] = NAV;

type NavItem = (typeof NAV)[number]["items"][number];
type ChildOf<Item> = Item extends { children: readonly (infer Child)[] } ? Child : never;
/** Every path the registry names, as literals, for the breadcrumb drift guard. */
export type TenantNavCoveredPath =
  | NavItem["to"]
  | ChildOf<NavItem>["to"]
  | ChildOf<ChildOf<NavItem>>["to"];

/**
 * The nav as one caller may see it: the pages their role reaches and the tenant's modules keep,
 * each hub's sections filtered the same way. `modules` null (still loading) keeps no module's
 * page, so a switched-off page never flashes in; the rest wait for nothing.
 */
export function visibleTenantNav(
  nav: readonly TenantNavGroup[],
  opts: { can: (role: TenantRole) => boolean; modules: TenantModules | null },
): TenantNavGroup[] {
  const hasModule = (entry: TenantNavChild) =>
    entry.module === undefined || opts.modules?.[entry.module] === true;
  return nav
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => opts.can(item.minRole) && hasModule(item))
        .map((item) =>
          item.children
            ? {
                ...item,
                children: item.children
                  .filter((child) => opts.can(child.minRole ?? item.minRole) && hasModule(child))
                  .map((child) =>
                    child.children
                      ? {
                          ...child,
                          children: child.children.filter(
                            (page) =>
                              opts.can(page.minRole ?? child.minRole ?? item.minRole) &&
                              hasModule(page),
                          ),
                        }
                      : child,
                  ),
              }
            : item,
        ),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * The module the page at `pathname` belongs to, from the nav entry whose path is the longest
 * prefix of it (a page's own module, else its section's, else its hub's); null for a page
 * outside every module.
 * Detail pages below a section (`storefront/chat/...`) resolve to that section.
 */
export function moduleForPathname(
  nav: readonly TenantNavGroup[],
  pathname: string,
  tenantId: string,
): TenantModuleKey | null {
  let best: { length: number; module: TenantModuleKey | null } = { length: 0, module: null };
  const consider = (to: string, module: TenantModuleKey | undefined) => {
    const path = to.replace("$tenantId", tenantId);
    if (pathname !== path && !pathname.startsWith(`${path}/`)) return;
    if (path.length > best.length) best = { length: path.length, module: module ?? null };
  };
  for (const group of nav) {
    for (const item of group.items) {
      consider(item.to, item.module);
      for (const child of item.children ?? []) {
        consider(child.to, child.module ?? item.module);
        for (const page of child.children ?? []) {
          consider(page.to, page.module ?? child.module ?? item.module);
        }
      }
    }
  }
  return best.module;
}
